import { PushNotificationSettings } from '@/components/notifications/PushNotificationSettings';
import { useState, type ReactNode } from 'react';
import { RefreshCw, MapPin, BellRing } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import type { ProfileWalkInDto } from '@/types/dto/walk-in.dto';

function describeEntry(entry: ProfileWalkInDto) {
  if (entry.status === 'CALLED')
    return {
      title: 'Chegou a sua vez!',
      label: 'Seu atendimento',
      value: 'Sua vez',
      description: 'Dirija-se ao profissional para iniciar.',
      instruction: 'Procure o profissional agora.',
      size: 'text-5xl sm:text-6xl',
    };
  if (entry.status === 'IN_ATTENDANCE')
    return {
      title: 'Bom atendimento!',
      label: 'Seu atendimento',
      value: 'Em curso',
      description: 'Seu atendimento já começou.',
      instruction: 'Seu atendimento está em andamento.',
      size: 'text-5xl sm:text-6xl',
    };
  return {
    title: 'Pronto, seu lugar está garantido!',
    instruction: 'Permaneça no estabelecimento.',
    size: 'text-8xl sm:text-9xl',
    label: 'Sua posição na fila',
    value: entry.position != null ? `${entry.position}º` : '—',
    description:
      entry.position === 1
        ? 'Você é o próximo. Fique por perto e aguarde a chamada.'
        : 'Aguarde sua vez no estabelecimento.',
  };
}

export default function WalkInConfirmation({
  companyName,
  companySlug,
  entry,
  ended,
  updating,
  error,
  onRefresh,
  onCancel,
  cancelling,
  children,
}: {
  companyName: string;
  companySlug: string;
  entry: ProfileWalkInDto;
  ended: boolean;
  updating: boolean;
  error: boolean;
  onRefresh: () => void;
  onCancel: () => Promise<void>;
  cancelling: boolean;
  children?: ReactNode;
}) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const status = describeEntry(entry);
  const inAttendance = entry.status === 'IN_ATTENDANCE';

  return (
    <section className="space-y-5 text-center">
      <div className="text-left" aria-live="polite">
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
          {ended ? 'Sua entrada foi encerrada' : status.title}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">{companyName}</p>
      </div>
      {ended ? (
        <p className="text-muted-foreground text-sm">Você não tem mais uma entrada ativa nesta fila.</p>
      ) : (
        <div className="grid items-start gap-5 md:grid-cols-[1.2fr_1fr]">
          <div className="border-primary/25 bg-card overflow-hidden rounded-3xl border shadow-sm">
            <div className="bg-primary-light text-primary flex items-center justify-between gap-2 px-5 py-3 text-left text-sm">
              <span className="flex items-center gap-2 font-semibold">
                <span
                  className={`h-2 w-2 rounded-full ${error ? 'bg-destructive' : 'bg-primary'}`}
                  aria-hidden="true"
                />
                {error ? 'Atualização interrompida' : 'Acompanhando sua fila'}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 shrink-0"
                aria-label="Atualizar posição"
                onClick={onRefresh}
                disabled={updating}
              >
                <RefreshCw className={updating ? 'animate-spin motion-reduce:animate-none' : ''} />
              </Button>
            </div>
            <div className="px-5 py-6 sm:p-8" aria-live="polite" aria-atomic="true">
              <p className="text-muted-foreground text-sm font-medium">{status.label}</p>
              <p className={`text-primary my-4 leading-none font-bold tracking-tighter tabular-nums ${status.size}`}>
                {status.value}
              </p>
              <p className="text-sm font-medium">{status.description}</p>
              {entry.professional?.name && (
                <p className="text-muted-foreground mt-4 text-sm">
                  Com <strong className="text-foreground">{entry.professional.name}</strong>
                </p>
              )}
            </div>
            <div className="border-border border-t px-5 py-4 text-left">
              <p className="text-muted-foreground flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                {status.instruction}
              </p>
              <p className="text-muted-foreground mt-2 text-xs">
                Sua posição é atualizada automaticamente enquanto esta tela estiver aberta.
              </p>
              {error && (
                <p role="alert" className="text-destructive mt-3 text-sm">
                  Não foi possível atualizar. A posição exibida pode estar desatualizada. Confira sua vez com o
                  profissional.
                </p>
              )}
            </div>
          </div>
          <div className="space-y-4 text-left">
            <PushNotificationSettings queue />
            <div className="bg-card border-border rounded-2xl border p-5">
              <p className="flex items-center gap-2 text-sm font-semibold">
                <BellRing className="text-primary h-4 w-4" aria-hidden="true" />
                Avisos pelo WhatsApp
              </p>
              <p className="text-muted-foreground mt-2 text-sm">
                Confira se o canal está ativado nas suas preferências.
              </p>
              <Link
                className="text-primary mt-2 inline-flex min-h-11 items-center text-sm font-medium underline"
                to="/perfil?tab=preferences"
              >
                Configurar avisos no app e WhatsApp
              </Link>
            </div>
            {children}
          </div>
        </div>
      )}
      <div className="space-y-3 border-t pt-5">
        <Button asChild variant="outline" className="h-12 w-full rounded-xl">
          <Link to={`/${companySlug}`}>Voltar à página do local</Link>
        </Button>
        {!ended &&
          !inAttendance &&
          (confirmCancel ? (
            <div className="bg-muted space-y-3 rounded-xl p-4">
              <p className="text-sm">Sair da fila? Você perderá sua posição.</p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  variant="outline"
                  className="h-11 flex-1"
                  onClick={() => setConfirmCancel(false)}
                  disabled={cancelling}
                >
                  Continuar na fila
                </Button>
                <Button
                  variant="destructive"
                  className="h-11 flex-1"
                  onClick={() => void onCancel()}
                  disabled={cancelling}
                >
                  {cancelling ? 'Saindo...' : 'Confirmar saída'}
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="ghost" className="text-muted-foreground h-11" onClick={() => setConfirmCancel(true)}>
              Sair da fila
            </Button>
          ))}
      </div>
    </section>
  );
}
